import { doc, getDoc, setDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { UserUsage } from '../types/limits';

export const getUserUsage = async (userId: string): Promise<UserUsage | null> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    const usageDoc = await getDoc(usageRef);

    if (usageDoc.exists()) {
      const data = usageDoc.data();
      return {
        ...data,
        lastResetDate: data.lastResetDate?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      } as UserUsage;
    }

    // Criar usage padrão se não existir
    const defaultUsage: Omit<UserUsage, 'lastResetDate' | 'updatedAt'> = {
      eventsThisMonth: 0,
      totalGuests: 0,
      totalRecipes: 0,
      totalInventoryItems: 0,
      guestGroups: 0,
      activeShoppingLists: 0,
      wishlistItems: 0,
      customCategories: 0,
    };

    const now = new Date();
    await setDoc(usageRef, {
      ...defaultUsage,
      lastResetDate: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return {
      ...defaultUsage,
      lastResetDate: now,
      updatedAt: now,
    };
  } catch (error) {
    console.error('Erro ao buscar usage:', error);
    return null;
  }
};

export const incrementEventCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      eventsThisMonth: increment(1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao incrementar eventos:', error);
    throw error;
  }
};

export const decrementEventCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      eventsThisMonth: increment(-1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao decrementar eventos:', error);
    throw error;
  }
};

export const incrementGuestCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      totalGuests: increment(1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao incrementar convidados:', error);
    throw error;
  }
};

export const decrementGuestCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      totalGuests: increment(-1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao decrementar convidados:', error);
    throw error;
  }
};

export const incrementRecipeCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      totalRecipes: increment(1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao incrementar receitas:', error);
    throw error;
  }
};

export const decrementRecipeCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      totalRecipes: increment(-1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao decrementar receitas:', error);
    throw error;
  }
};

export const incrementInventoryCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');

    // Verificar se o documento existe
    const usageDoc = await getDoc(usageRef);

    if (!usageDoc.exists()) {
      // Criar documento se não existir
      await setDoc(usageRef, {
        eventsThisMonth: 0,
        totalGuests: 0,
        totalRecipes: 0,
        totalInventoryItems: 1,
        guestGroups: 0,
        activeShoppingLists: 0,
        wishlistItems: 0,
        customCategories: 0,
        lastResetDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } else {
      // Incrementar se existir
      await updateDoc(usageRef, {
        totalInventoryItems: increment(1),
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Erro ao incrementar inventário:', error);
    throw error;
  }
};

export const decrementInventoryCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      totalInventoryItems: increment(-1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao decrementar inventário:', error);
    throw error;
  }
};

export const incrementCustomCategoryCount = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      customCategories: increment(1),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao incrementar categorias customizadas:', error);
    throw error;
  }
};

export const resetMonthlyCounters = async (userId: string): Promise<void> => {
  try {
    const usageRef = doc(db, 'users', userId, 'usage', 'current');
    await updateDoc(usageRef, {
      eventsThisMonth: 0,
      lastResetDate: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Erro ao resetar contadores mensais:', error);
    throw error;
  }
};

export const getUsageStats = getUserUsage;
